import { isEmpty, get } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from 'tss-react/mui';
import { Add, Delete } from '@mui/icons-material';
import { IconButton, Rating } from '@mui/material';
import TextInput from '../ReusableComponents/TextInput/TextInput';
import SelectInput from '../ReusableComponents/SelectInput/SelectInput';
import Button from '../ReusableComponents/Button/Button';

const useStyles = makeStyles()(() => ({
  row: {
    alignItems: 'center',
    display: 'flex',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  addButton: {
    margin: '20px',
  },
  removeButton: {
    backgroundColor: 'white',
    color: 'black',
    borderRadius: '8px',
    border: '1px solid grey',
    width: '50px',
    height: '50px',
  },
}));

const platformOptions = [
  { text: 'Instagram', value: 'Instagram' },
  { text: 'TikTok', value: 'TikTok' },
  { text: 'YouTube', value: 'YouTube' },
  { text: 'Other', value: 'Other' },
];

// Do not use React hooks like useState/useMemo/useEffect in this component as it is used in an array that use indexes as keys.
export function InfluencerHandle(props) {
  const { classes } = useStyles();
  const {
    id,
    primary,
    social_media_platform,
    social_media_handle,
    disabled,
    onSetPrimary,
    onChange,
    onRemove,
  } = props;

  const options = [
    ...platformOptions,
    ...(social_media_platform &&
    !platformOptions.some((o) => o.value === social_media_platform)
      ? [
          {
            text: `${social_media_platform} (Custom)`,
            value: social_media_platform,
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col md:flex-row gap-4 md:items-center">
      <Rating
        max={1}
        value={primary ? 1 : 0}
        disabled={disabled}
        onChange={(_, v) => {
          if (v === 1 && !primary) onSetPrimary(id);
        }}
      />
      <SelectInput
        fullWidth
        required
        disabled={disabled}
        error={isEmpty(social_media_platform)}
        native
        label="Influencer Platform"
        onChange={(e) => onChange(id, 'social_media_platform', e.target.value)}
        value={social_media_platform || ''}
        id={`va-influencer-platform-${id}`}
        autoComplete="no"
        options={options}
      />
      <TextInput
        fullWidth
        required
        disabled={disabled}
        error={isEmpty(social_media_handle)}
        label="Influencer Handle"
        variant="outlined"
        onChange={(e) => onChange(id, 'social_media_handle', e.target.value)}
        value={social_media_handle || ''}
        id={`va-influencer-handle-${id}`}
        autoComplete="no"
      />
      <IconButton
        onClick={() => onRemove(id)}
        className={classes.removeButton}
        disabled={primary || disabled}
      >
        <Delete fontSize="small" />
      </IconButton>
    </div>
  );
}

export default function InfluencerFields(props) {
  const { classes } = useStyles();
  const influencerHandles = get(props.form, 'influencer_handles')?.length
    ? get(props.form, 'influencer_handles')
    : [
        {
          primary: true,
          social_media_platform: '',
          social_media_handle: '',
        },
      ];

  const handleChange = (idx, name, value) => {
    const updatedHandles = influencerHandles.map((handle, index) =>
      index === idx ? { ...handle, [name]: value } : handle,
    );
    props.onChange('influencer_handles', updatedHandles);
  };

  const handleSetPrimary = (idx) => {
    const updatedHandles = influencerHandles.map((handle, index) =>
      index === idx
        ? { ...handle, primary: true }
        : { ...handle, primary: false },
    );
    props.onChange('influencer_handles', updatedHandles);
  };

  const handleAddHandle = () => {
    props.onChange('influencer_handles', [
      ...influencerHandles,
      { primary: false, social_media_platform: '', social_media_handle: '' },
    ]);
  };

  const handleRemoveHandle = (idx) => {
    const updatedHandles = influencerHandles.filter(
      (_, index) => index !== idx,
    );
    props.onChange('influencer_handles', updatedHandles);
  };

  return (
    <>
      <div className={classes.column}>
        {influencerHandles.map((handle, idx) => (
          <InfluencerHandle
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            id={idx}
            {...handle}
            disabled={props.disabled}
            onSetPrimary={handleSetPrimary}
            onChange={handleChange}
            onRemove={handleRemoveHandle}
          />
        ))}
      </div>
      {!props.disabled && (
        <Button
          variant="outlined"
          color="primary"
          small
          flex
          icon
          onClick={handleAddHandle}
          disabled={props.disabled}
          className="my-4 md:ml-10"
        >
          <Add />
          Add another
        </Button>
      )}
    </>
  );
}

InfluencerFields.propTypes = {
  disabled: PropTypes.bool,
  form: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
};
